import React from 'react';
import {Button} from 'react-bootstrap';
import {Loader} from 'shared/helpers';
import {ProductBackgroundImage} from 'components/customer/Product/ProductStyled';
import {ItemColumn} from 'shared/components/Product/ItemColumn';
import styled from 'styled-components';
import {QuickAdd} from 'components/customer/Product/QuickAdd';
import {useItem} from 'shared/components/Product/helpers/useItem';
import {isDeviceMedium} from 'shared/helpers/DeviceSize';
import {useJobContext} from 'contexts/JobContext';
import {PartialRoom} from 'shared/types/PartialRoom';
import {useMouseOver} from 'shared/helpers/useMouseOver';
import {isBrowser} from 'react-device-detect';
import Icon from 'shared/Icon';
import {HardwareControls} from 'shared/components/Product/HardwareControls';

export type ItemType = {
    favourites: number;
    id: number;
    sundryCategoryId: number;
    name: string;
    changed_name: string;
    changedImage: string;
    imageUrl: string;
    stockStatus: number;
    stockStatusText: string;
    advanced?: boolean;
    hidden: boolean;
    defaultWidth: number;
    defaultDepth: number;
    maxWidth?: number;
    minWidth?: number;
    lshape?: number;
    leftWidth?: number;
    productType?: number;
    style?: number;
    template_3d?: string[];
    subStyleId?: string;
    dimensions?: string;
};

type ItemProps = {
    item: ItemType;
    isProduct: boolean;
    setError?: (errors: string[]) => void;
    loader?: boolean;
};

export const Item = ({item, isProduct, setError}: ItemProps) => {
    const mediumDevice = isDeviceMedium();
    const {room} = useJobContext() as {room: PartialRoom};
    const {isMouseOverComponent, handleMouseEnter, handleMouseExit} =
        useMouseOver(100);
    const {
        loading,
        isFavourite,
        style,
        favouriteButtonDisabled,
        itemClickHandler,
        addToFavouriteHandler,
    } = useItem(item, isProduct);

    return (
        <>
            <Loader loader={loading} />
            {!item.hidden && (
                <ItemColumn $hardware={!isProduct}>
                    <HardwareItem
                        id={`item-id-${item.id}`}
                        $isActive={isMouseOverComponent}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseExit}>
                        <Details
                            onClick={isBrowser ? itemClickHandler : undefined}
                            className={
                                (isProduct
                                    ? 'details'
                                    : 'details hardware-details') +
                                (mediumDevice ? ` no-padding` : '')
                            }>
                            <Favourite
                                onClick={addToFavouriteHandler}
                                disabled={favouriteButtonDisabled}>
                                <Icon
                                    className="favourite-icon-base"
                                    iconName={
                                        isFavourite
                                            ? 'favourite.svg'
                                            : 'favourite-white.svg'
                                    }
                                />
                                {!isFavourite && (
                                    <Icon
                                        className="favourite-icon-hover"
                                        iconName="favourite.svg"
                                    />
                                )}
                            </Favourite>

                            <ImageContainer>
                                {item.hasOwnProperty('imageUrl') ? (
                                    <ProductBackgroundImage
                                        alt={item.name}
                                        backgroundImage={
                                            style.backgroundImage || ''
                                        }
                                        transform={parseInt(room.extHorGrain)}
                                        src={item.changedImage}
                                        fallbackSrc={item.imageUrl}
                                        resize={true}
                                        isHardware={!isProduct}
                                    />
                                ) : null}
                            </ImageContainer>

                            <Label className="item-name">
                                <div>
                                    {item.changed_name
                                        ? item.changed_name
                                        : item.name}{' '}
                                    {item.hasOwnProperty('stockStatus') &&
                                    item.stockStatus == 1
                                        ? ` (${item.stockStatusText})`
                                        : ''}
                                </div>
                                {item.dimensions ? (
                                    <Dimension>{item.dimensions}</Dimension>
                                ) : null}
                            </Label>
                        </Details>

                        <HardwareControls
                            item={item}
                            isProduct={isProduct}
                            setError={setError}
                        />

                        {isProduct ? (
                            <QuickAdd
                                item={item}
                                isMouseOver={isMouseOverComponent}
                                onClickHandler={itemClickHandler}
                                onMouseLeave={handleMouseExit}
                            />
                        ) : null}
                    </HardwareItem>
                </ItemColumn>
            )}
        </>
    );
};

const Dimension = styled.div`
    font-size: 0.9em;
    font-weight: 400;
    color: rgb(75, 74, 74);
`;

const Favourite = styled(Button)`
    position: absolute;
    top: 0;
    right: 15px;
    z-index: 3;
    background: white !important;
    border: 0;
    padding: 0;
    width: 20px !important;
    height: 31px;

    > img {
        width: 20px !important;
        vertical-align: initial;
    }

    .favourite-icon-hover {
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
    }

    &:hover .favourite-icon-hover {
        opacity: 1;
    }

    &:hover,
    &:active,
    &:focus {
        background: white !important;
    }
`;

const HardwareItem = styled.div<{$isActive?: boolean}>`
    position: relative;
    border: 1px solid #e4e2db;
    border-radius: 10px;
    padding: 10px;
    background: #fdfcfa;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-shadow:
        0 1px 3px rgba(0, 0, 0, 0.06),
        0 3px 10px rgba(0, 0, 0, 0.06);
    transition:
        box-shadow 180ms ease,
        transform 180ms ease;

    ${({$isActive, theme}) => {
        if ($isActive) {
            return `
                z-index: 11 !important;
                border-color: ${theme.colors.secondary?.main};
                box-shadow:
                    0 6px 16px rgba(0, 0, 0, 0.1),
                    0 14px 32px rgba(0, 0, 0, 0.14);
                transform: translateY(-2px);
            `;
        }
    }}

    &:hover, &:active, &:focus {
        border-color: ${({theme}) => theme.colors.secondary?.main};
        box-shadow:
            0 6px 16px rgba(0, 0, 0, 0.1),
            0 14px 32px rgba(0, 0, 0, 0.14);
        transform: translateY(-2px);
    }
`;

const Label = styled.div`
    font-size: 0.9em;
    margin: 5px 0;
    font-weight: 500;
    min-height: 44px;
    color: rgb(75, 74, 74);
`;

const Details = styled.div`
    position: relative;
    display: flex;

    > button {
        position: absolute;
    }

    flex-direction: column;
`;

const ImageContainer = styled.div`
    width: 100%;
    height: 150px;

    flex: 1;
    align-content: center;
    padding: 10px;
    border-radius: 10px;
    border: 1px solid #dfe3e8;

    img {
        width: initial !important;
    }
`;
